import Router from 'vue-router';
import Vue from 'vue';
import store from '@/store';
import loggedInRoutes from './routes/loggedIn';
import loggedOffRoutes from './routes/loggedOff';
import { onMaintenance, isUserAuthenticated, isPathValid } from './routes/util';

const MainLayout = () => import(/* webpackPrefetch: true */ '@/layout/main');
const Dashboard = () => import(/* webpackChunkName: "pages" */ '@/pages/dashboard');
const Profiles = () => import(/* webpackChunkName: "pages" */ '@/pages/profiles');
const LicenseAgreement = () => import(/* webpackChunkName: "pages" */ '@/pages/licenseAgreement');
const Data = () => import(/* webpackChunkName: "pages" */ '@/pages/data');
const Documents = () => import(/* webpackChunkName: "pages" */ '@/pages/documents');
const Events = () => import(/* webpackChunkName: "pages" */ '@/pages/events');
const Shareholders = () => import(/* webpackChunkName: "pages" */ '@/pages/shareholders');
const Participants = () => import(/* webpackChunkName: "pages" */ '@/pages/marketMakers');
const SPRReport = () => import(/* webpackChunkName: "pages" */ '@/pages/sprReport');
const NOBOReport = () => import(/* webpackChunkName: "pages" */ '@/pages/noboReport');
const Campaigns = () => import(/* webpackChunkName: "pages" */ '@/pages/campaigns');
const IRFirms = () => import(/* webpackChunkName: "pages" */ '@/pages/irFirms');
const Chatbot = () => import(/* webpackChunkName: "chatbot" */ '@/pages/chatbot-v2');
const Maintenance = () => import(/* webpackChunkName: "maintenance" */ '@/pages/maintenance');
const Contacts = () => import(/* webpackChunkName: "pages" */ '@/pages/contacts');
const SituationRoom = () => import(/* webpackChunkName: "pages" */ '@/pages/situationRoom');
const TransactionHistory = () => import(/* webpackChunkName: "pages" */ '@/pages/transactionHistory');
const InstitutionalInvestors = () => import(/* webpackChunkName: "pages" */ '@/pages/institutionalInvestors');
const AdvancedSearch = () => import(/* webpackChunkName: "pages" */ '@/pages/institutionalInvestors/advancedSearch');
const InvestorView = () => import(/* webpackChunkName: "pages" */ '@/pages/institutionalInvestors/investorView');
const Watchlist = () => import(/* webpackChunkNAme: "pages" */ '@/pages/institutionalInvestors/watchlist');
const EmailPreview = () => import(/* webpackChunkName "email" */ '@/pages/emailCampaigns/emailPreview');
const CIL = () => import(/* webpackChunkName: "pages" */ '@/pages/contactInfoLookup');
const ShelfCapacity = () => import(/* webpackChunkName: "shelf-capacity" */ '@/pages/shelfCapacity');
const Performance = () => import(/* webpackChunkName: "pages" */ '@/pages/performance');
// const IrWebTool = () => import(
// /* webpackChunkName: "pages" */ '@/pages/irWebTool');
// const LivePreview = () => import(
// /* webpackChunkName: "pages" */ '@/pages/irWebTool/LivePreview');
// const ShortInterest = () => import(
// /* webpackChunkName: "shortInterest" */ '@/pages/shortInterest');
const Analytics = () => import(/* webpackChunkName: "analytics" */ '@/pages/analytics');
const Iborrowdesk = () => import(/* webpackChunkName: "iborrowdesk" */ '@/pages/iborrowdesk');

Vue.use(Router);

const originalPush = Router.prototype.push;
Router.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch((err) => err);
};

/**
 * Check if user is authenticated from user store
 * @return {Boolean}
 */

const router = new Router({
  base: '/',
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'MainLayout',
      component: MainLayout,
      children: [
        {
          path: 'home',
          name: 'Dashboard',
          alias: '',
          component: Dashboard,
        },
        {
          path: 'profiles',
          name: 'Profile',
          component: Profiles,
        },
        {
          path: 'licenseAgreement',
          name: 'License agreement',
          component: LicenseAgreement,
        },
        {
          path: 'data',
          name: 'Data',
          component: Data,
        },

        {
          path: 'documents',
          name: 'Documents',
          component: Documents,
        },
        {
          path: 'contacts/list',
          name: 'Contact Lists',
          component: Events,
        },
        {
          path: 'shareholders/:contactlookup?',
          name: 'Shareholders',
          component: Shareholders,
        },
        {
          path: 'participants',
          name: 'Participants',
          component: Participants,
        },
        {
          path: 'insights/participants',
          name: 'Insights  /  Participants',
          component: SPRReport,
        },
        {
          path: 'insights/shareholders',
          name: 'Insights  /  Shareholders',
          component: NOBOReport,
        },
        {
          path: 'campaigns',
          name: 'Campaigns',
          component: Campaigns,
        },
        {
          path: 'irfirms',
          name: 'IR Firms',
          component: IRFirms,
        },
        {
          path: 'chatbot',
          name: 'Chatbot',
          component: Chatbot,
        },
        {
          path: 'chatbot',
          name: 'Sequire chatbot',
          component: Chatbot,
        },
        {
          path: 'contacts/:event_id?/:contactlookup?',
          name: 'Contacts',
          component: Contacts,
        },
        {
          path: 'situationRoom',
          name: 'Situation Room',
          component: SituationRoom,
        },
        {
          path: 'payment',
          name: 'Payment',
          component: TransactionHistory,
        },
        {
          path: 'institutionalInvestors/watchlist',
          name: 'Institutional Investors / Watchlist',
          component: Watchlist,
        },
        {
          path: 'institutionalInvestors/AdvancedSearch',
          name: 'Institutional Investors / Advanced search',
          component: AdvancedSearch,
        },
        {
          path: 'institutionalInvestors/investorView/:company_cik?/:investor_cik?/:is_fund?/:id?',
          name: 'Institutional Investors / Investor View',
          component: InvestorView,
        },
        {
          path: 'institutionalInvestors/:company_cik?',
          name: 'Institutional Investors',
          component: InstitutionalInvestors,
        },
        {
          // Retired with the old Screener: a bookmark or a menu entry lands on the investors page.
          path: 'investmentadvisers',
          redirect: { name: 'Institutional Investors' },
        },
        {
          path: 'contactlookup',
          name: 'Contact Info Lookup',
          component: CIL,
        },
        {
          path: 'shelf',
          name: 'Shelf Capacity',
          component: ShelfCapacity,
        },
        {
          // The address the new page had while it ran beside the old one.
          path: 'shelf-v2',
          redirect: { name: 'Shelf Capacity' },
        },
        {
          path: 'performance',
          name: 'Performance',
          component: Performance,
        },
        // {
        //   path: 'shortinterest',
        //   name: 'Short Interest',
        //   component: ShortInterest,
        // },
        /* {
          path: 'webtools',
          name: 'IR Web Builder',
          component: IrWebTool,
        }, */
        {
          path: 'analytics',
          name: 'Analytics',
          component: Analytics,
        },
        {
          path: 'shortinterest',
          name: 'Short Interest',
          component: Iborrowdesk,
        },
      ].concat(loggedInRoutes),
      beforeEnter: (to, from, next) => {
        if (onMaintenance()) next('/maintenance');
        else if (!isUserAuthenticated()) next('/login');
        else if (isPathValid(to)) next();
        else next('/home');
      },
    },
    ...loggedOffRoutes,
    /*
      path: '/webtools/livepreview',
      name: 'IR Web - Live Preview',
      component: LivePreview,
      beforeEnter: (to, from, next) => {
        if (onMaintenance()) next('/maintenance');
        else if (!isUserAuthenticated()) next('/login');
        else if (isPathValid(to)) next();
        else next('/home');
      },
    }, */
    {
      path: '/emailcampaigns/preview/:type/:id?',
      name: 'Email Campaigns / Preview',
      component: EmailPreview,
      beforeEnter: (to, from, next) => {
        if (onMaintenance()) next('/maintenance');
        else if (!isUserAuthenticated()) next('/login');
        else if (isPathValid(to)) next();
        else next('/home');
      },
    },
    {
      path: '/maintenance',
      name: 'Maintenance',
      component: Maintenance,
      beforeEnter: (to, from, next) => {
        if (!onMaintenance()) next('/home');
        else next();
      },
    },
    {
      path: '*',
      redirect: '/',
    },
  ],
});

router.beforeEach((to, from, next) => {
  const host = window.location.origin.toLowerCase();
  const oldHost = '.sraxir.com';
  if (host.includes(oldHost)) {
    const newHost = '.mysequire.com';
    let location = host.replace(oldHost, newHost);
    location = `${location}${to.fullPath}`;
    window.location = location;
  } else {
    next();
  }
});

router.afterEach((to, from) => {
  const { commit, dispatch } = store;
  commit('currentView', to.path);
  commit('preview', from.path);
  dispatch({
    type: 'breadcrumbs/updateBreadcrumbs',
    breadcrumbs: {
      header: to.name,
      trail: ['Home', to.name],
    },
  });
});

export default router;
